import type { AxiosError } from 'axios'
import { isAxiosError } from 'axios'
import { i18n } from '@/i18n'
import { isDeviceOnline } from '@/shared/composables/use-network-status'

type LaravelValidationBody = {
  message?: string
  errors?: Record<string, string[]>
}

function requestUrlHint(axiosErr: AxiosError): string {
  const url = String(axiosErr.config?.url ?? '')
  const base = String(axiosErr.config?.baseURL ?? '')
  return `${base} ${url}`.toLowerCase()
}

function isHeavyExcelImportRequest(axiosErr: AxiosError): boolean {
  const hint = requestUrlHint(axiosErr)
  if (
    /factory-import|excel|spreadsheet|xlsx|hr\/users\/import|registration-sync\/(export|confirm-imported)/.test(
      hint,
    )
  ) {
    return true
  }
  return Number(axiosErr.config?.timeout ?? 0) >= 60_000
}

function timeoutMessage(axiosErr: AxiosError): string {
  return i18n.global.t(
    isHeavyExcelImportRequest(axiosErr) ? 'common.excelRequestTimeout' : 'common.requestTimeout',
  )
}

/** Gateway / network blips that are safe to auto-retry once. */
export function isTransientApiFailure(err: unknown): boolean {
  if (!isAxiosError(err)) {
    if (err instanceof Error) {
      const msg = err.message.toLowerCase()
      return (
        msg.includes('network')
        || msg.includes('timeout')
        || msg.includes('abort')
        || msg.includes('offline')
        || msg.includes('refresh lock')
      )
    }
    return false
  }

  if (!err.response) {
    return true
  }

  const status = err.response.status
  return status === 408 || status === 429 || status === 502 || status === 503 || status === 504
}

function laravelErrorBody(err: unknown): LaravelValidationBody | null {
  if (!err || typeof err !== 'object') return null
  if ('isAxiosError' in err && (err as AxiosError).isAxiosError) {
    const data = (err as AxiosError<LaravelValidationBody>).response?.data
    return data && typeof data === 'object' ? data : null
  }
  if ('response' in err) {
    const data = (err as { response?: { data?: LaravelValidationBody } }).response?.data
    return data && typeof data === 'object' ? data : null
  }
  return null
}

/** Message hiển thị cho user từ response Laravel / lỗi mạng. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object' || !('isAxiosError' in err) || !(err as AxiosError).isAxiosError) {
    const body = laravelErrorBody(err)
    if (body) {
      const firstFieldError = body.errors
        ? Object.entries(body.errors)
          .filter(([key]) => key !== 'existing_statement_id')
          .flatMap(([, messages]) => messages)[0]
        : undefined
      return firstFieldError ?? body.message ?? (err instanceof Error ? err.message : fallback)
    }
    if (err instanceof Error && err.message.trim()) {
      return err.message
    }
    return fallback
  }

  const axiosErr = err as AxiosError<LaravelValidationBody>

  if (!axiosErr.response) {
    if (!isDeviceOnline()) {
      return i18n.global.t('common.offlineHint')
    }
    if (axiosErr.code === 'ECONNABORTED' || /timeout/i.test(String(axiosErr.message ?? ''))) {
      return timeoutMessage(axiosErr)
    }
    // Proxy/FPM kill on long imports often surfaces as ERR_NETWORK (no response body).
    if (axiosErr.code === 'ERR_NETWORK') {
      const configuredTimeout = Number(axiosErr.config?.timeout ?? 0)
      if (configuredTimeout >= 60_000) {
        return timeoutMessage(axiosErr)
      }
      return i18n.global.t('common.networkError')
    }
    // Cancelled / aborted / empty-code blips — same user-facing copy as network.
    return i18n.global.t('common.networkError')
  }

  const status = axiosErr.response.status
  if (status === 408 || status === 429 || status === 502 || status === 503 || status === 504) {
    return timeoutMessage(axiosErr)
  }
  // Nginx auth_request / upstream HTML 500 often has no Laravel `message`.
  if (status >= 500) {
    return i18n.global.t('common.apiUnreachable')
  }

  const body = axiosErr.response.data
  const firstFieldError = body?.errors
    ? Object.entries(body.errors)
      .filter(([key]) => key !== 'existing_statement_id')
      .flatMap(([, messages]) => messages)[0]
    : undefined

  return firstFieldError ?? body?.message ?? fallback
}

/** First Laravel validation message for a single field. */
export function apiFieldError(err: unknown, field: string): string | undefined {
  const body = laravelErrorBody(err)
  const messages = body?.errors?.[field]
  if (!Array.isArray(messages) || messages.length === 0) return undefined
  const first = messages[0]
  return typeof first === 'string' && first.trim() ? first : undefined
}

/** First validation error id for a field (e.g. existing_statement_id on overlap). */
export function apiValidationErrorId(err: unknown, field: string): number | null {
  if (!err || typeof err !== 'object' || !('isAxiosError' in err) || !(err as AxiosError).isAxiosError) {
    return null
  }
  const axiosErr = err as AxiosError<LaravelValidationBody>
  const raw = axiosErr.response?.data?.errors?.[field]?.[0]
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : null
}

/** First Laravel validation message for every field. */
export function apiValidationErrors(err: unknown): Record<string, string> {
  if (!err || typeof err !== 'object' || !('isAxiosError' in err) || !(err as AxiosError).isAxiosError) {
    return {}
  }

  const axiosErr = err as AxiosError<LaravelValidationBody>
  const errors = axiosErr.response?.data?.errors
  if (!errors) return {}

  return Object.fromEntries(
    Object.entries(errors)
      .filter(([, messages]) => Array.isArray(messages) && messages.length > 0)
      .map(([field, messages]) => [field, messages[0]]),
  )
}

/** Dịch error_message lưu trong DB khi backend ghi nhầm raw key (vd. api.hr.import_invalid_phone). */
export function translateStoredApiMessage(message: string | null | undefined): string {
  if (!message?.trim()) return '—'
  const trimmed = message.trim()
  if (!trimmed.startsWith('api.')) return trimmed

  const i18nKey = `apiMessages.${trimmed.slice(4)}`
  const { t, te } = i18n.global
  return te(i18nKey) ? t(i18nKey) : trimmed
}
